import React, { useState } from "react";
import { Table, Button, Badge } from "reactstrap";
import SettingsManagementApi from "../../api/SettingsManagementApi";
import Loader from "../../components/Loader";
import Notification from "../../components/Notification";
import { DATETIME_FORMAT } from "../../constants";
import { toDrDateTime } from "../../util/dates";
import useLoader from "../../util/useLoader";
import useResourceLoader from "../../util/useResourceLoader";

const STATUS = {
    success: { color: 'success', label: 'Exitosa' },
    partial: { color: 'warning', label: 'Parcial' },
    failed: { color: 'danger', label: 'Fallida' },
};

const TRIGGER = {
    scheduled: 'Programada',
    startup: 'Inicio del sistema',
    manual: 'Manual',
};

function parseDetails(details) {
    try {
        return JSON.parse(details) || [];
    } catch (e) {
        return [];
    }
}

function duration(entry) {
    if (!entry.finishedAt) return '-';
    // Both timestamps share the same (UTC) offset, so plain Date parsing is enough for the difference.
    const seconds = (Date.parse(entry.finishedAt) - Date.parse(entry.startedAt)) / 1000;
    return Number.isFinite(seconds) ? `${seconds.toFixed(1)} s` : '-';
}

/** Runs of the UCS contact agent sync (#10403), newest first. */
function UcsContactAgentSyncLog() {
    const [reloadCt, setReloadCt] = useState(0);
    const [entries, loadingEntries, errorLoadingEntries] = useResourceLoader(
        () => SettingsManagementApi.getUcsContactAgentSyncLog({ take: 50 }),
        [reloadCt],
        []
    );
    const [running, errorRunning, runFn] = useLoader();
    const [queued, setQueued] = useState(false);
    const [expanded, setExpanded] = useState(null);

    function runNow() {
        runFn(async () => {
            await SettingsManagementApi.runUcsContactAgentSync();
            setQueued(true);
        });
    }

    return (<>
        <div className="my-3">
            <p className="text-muted">
                La lista de agentes de contacto UCS se sincroniza diariamente a la 1:00 a.m. y al iniciar el sistema.
                Si una sucursal falla o UCS no devuelve agentes, se mantiene la lista anterior de esa sucursal.
            </p>
            <Button color="primary" disabled={running} onClick={runNow}>Ejecutar ahora</Button>{' '}
            <Button color="secondary" outline disabled={loadingEntries} onClick={() => { setQueued(false); setReloadCt(reloadCt + 1); }}>Actualizar</Button>
            {queued ? <span className="ml-3 text-muted">{"Sincronización en cola. Presione \"Actualizar\" en unos segundos para ver el resultado."}</span> : null}
            {errorRunning ? <Notification error={errorRunning} /> : null}
        </div>
        {loadingEntries ? (
            <Loader />
        ) : errorLoadingEntries ? (
            <Notification error={errorLoadingEntries} />
        ) : !entries?.length ? (
            <Notification color="info">No hay ejecuciones registradas.</Notification>
        ) : (
            <Table size="sm" hover responsive>
                <thead>
                    <tr>
                        <th>Inicio</th>
                        <th>Duración</th>
                        <th>Origen</th>
                        <th>Estado</th>
                        <th>Nuevos</th>
                        <th>Actualizados</th>
                        <th>Desactivados</th>
                        <th>Agentes activos</th>
                        <th>Sucursales con error</th>
                        <th>Sucursales omitidas</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {entries.map(entry => {
                        const status = STATUS[entry.status] || { color: 'secondary', label: entry.status };
                        const isOpen = expanded === entry.ucsContactAgentSyncLogId;
                        return (<React.Fragment key={entry.ucsContactAgentSyncLogId}>
                            <tr>
                                <td>{toDrDateTime(entry.startedAt).format(DATETIME_FORMAT)}</td>
                                <td>{duration(entry)}</td>
                                <td>{TRIGGER[entry.trigger] || entry.trigger}</td>
                                <td><Badge color={status.color}>{status.label}</Badge></td>
                                <td>{entry.inserted}</td>
                                <td>{entry.updated}</td>
                                <td>{entry.deactivated}</td>
                                <td>{entry.activeAgents ?? '-'}</td>
                                <td>{entry.failedBranches || '-'}</td>
                                <td>{entry.skippedBranches || '-'}</td>
                                <td>
                                    <Button size="sm" color="link" onClick={() => setExpanded(isOpen ? null : entry.ucsContactAgentSyncLogId)}>
                                        {isOpen ? 'Ocultar' : 'Detalle'}
                                    </Button>
                                </td>
                            </tr>
                            {isOpen ? (
                                <tr>
                                    <td colSpan="11">
                                        <SyncLogDetails entry={entry} />
                                    </td>
                                </tr>
                            ) : null}
                        </React.Fragment>);
                    })}
                </tbody>
            </Table>
        )}
    </>);
}

function SyncLogDetails({ entry }) {
    const branches = parseDetails(entry.details);
    return (<>
        {entry.errorMessage ? <Notification color="danger">{entry.errorMessage}</Notification> : null}
        <Table size="sm" bordered className="mb-0">
            <thead>
                <tr>
                    <th>Sucursal</th>
                    <th>Recibidos de UCS</th>
                    <th>Nuevos</th>
                    <th>Actualizados</th>
                    <th>Desactivados</th>
                    <th>Resultado</th>
                </tr>
            </thead>
            <tbody>
                {branches.map(branch => (
                    <tr key={branch.branchCode}>
                        <td>{branch.branchCode} ({branch.branchId})</td>
                        <td>{branch.error ? '-' : branch.received}</td>
                        <td>{branch.inserted}</td>
                        <td>{branch.updated}</td>
                        <td>{branch.deactivated}</td>
                        <td>{branch.error ? (
                            <span className="text-danger">Error: {branch.error}</span>
                        ) : branch.skipped ? (
                            <span className="text-warning">UCS no devolvió agentes; se mantuvo la lista anterior</span>
                        ) : 'OK'}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    </>);
}

export default UcsContactAgentSyncLog;
